import React, { useState, useEffect } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
// icons
import { AiOutlineEye } from 'react-icons/ai';
// components
import Button from 'components/backoffice/buttonCustom';
import Input from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import WatchModal from 'components/backoffice/modals/WatchModal';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const TypeScheduleForm = ({
  isEditing,
  onSubmitForm,
  fileInputRef,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  beforeTitle,
}) => {
  const { setTitle } = useOutletContext();
  const { t } = useTranslation(['backoffice']);
  const [isLoading, setIsLoading] = useState(false);
  const [openModalImage, setOpenModalImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const {
    formState: { errors },
    setValue,
    reset,
    watch,
    control,
    handleSubmit,
    getValues,
  } = useForm();
  const iconValue = watch('icon');

  const getImageForModal = () => {
    if (previewImage) return previewImage;
    if (typeof iconValue === 'string') return iconValue;
    if (iconValue instanceof Blob || iconValue instanceof File) {
      return URL.createObjectURL(iconValue);
    }
    return '';
  };

  const watchItem = () => {
    setOpenModalImage(true);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  let item;
  const image = getValues('icon');

  if (isEditing) {
    item =
      typeof image === 'string'
        ? image
        : image instanceof Blob || image instanceof File
          ? URL.createObjectURL(image)
          : '';
  } else {
    item = image !== undefined ? URL.createObjectURL(image) : '';
  }

  const hasValidDefaultValues = (values) => {
    return Object.values(values || {}).some(
      (v) => v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && Object.keys(v || {}).length === 0),
    );
  };

  const verificateNullValue = (value) => {
    return value !== null && value !== undefined ? value : '';
  };

  useEffect(() => {
    if (!hasValidDefaultValues(defaultValues)) return;
    if (isEditing) {
      setValue('icon', null);
      if (defaultValues?.icon)
        fetch(defaultValues.icon)
          .then((response) => response.blob())
          .then((blob) => {
            const name = defaultValues.icon.split('?')[0].split('/').pop();
            const file = new File([blob], name, { type: `image/${name.split('.')[1]}` });
            setValue('icon', file);
          })
          .catch((error) => {
            console.error('Error al obtener la imagen:', error);
          });
    } else {
      setValue('icon', defaultValues.icon);
    }
    setValue('name', verificateNullValue(defaultValues.name));
  }, [defaultValues]);

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={4} className="mb-md-2 mb-lg-0">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <Input value={value} type="text" label={t('name')} onChange={onChange} placeholder={t('name')} />
              )}
            />
            {errors?.name && <small className="text-danger">{errors.name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={4} className="mt-4 mt-lg-0 mb-md-2 mb-lg-0 d-flex gap-3">
            <div className="w-100">
              <Controller
                name="icon"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="file"
                    dropFileMsg={t('dropFile')}
                    label={t('icon')}
                    placeholder={t('selectImg')}
                    accept="image/*"
                    value={value}
                    onChange={(e) => {
                      const file = e instanceof File ? e : e?.target?.files?.[0];
                      if (file) {
                        setPreviewImage(URL.createObjectURL(file));
                      } else {
                        setPreviewImage(null);
                      }
                      onChange(e);
                    }}
                    ref={fileInputRef}
                  />
                )}
              />
            </div>
            <div className="mt-4-3">
              <Button type="button" color="terciary">
                <AiOutlineEye color="white" onClick={watchItem} />
              </Button>
            </div>
          </Col>
        </Row>
        <div className="mt-4 d-flex justify-content-end gap-3">
          <Button color="primary" onClick={handleSubmit(onSubmitForm)} loading={isLoading}>
            {t('save')}
            <SpinnerComponent loading={isLoading} />
          </Button>
          {isEditing && (
            <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={isLoading}>
              {t('eliminar')}
            </Button>
          )}
        </div>
      </Form>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deletetypeschedule')}
        beforeTitle={t('edit', { title: defaultValues?.name })}
      />
      <WatchModal
        item={getImageForModal()}
        title={t('icon')}
        show={openModalImage}
        setShow={setOpenModalImage}
        beforeTitle={defaultValues ? beforeTitle : t('edit', { title: defaultValues?.name })}
      />
    </div>
  );
};

TypeScheduleForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.string,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  isCleanInputs: PropTypes.bool,
  beforeTitle: PropTypes.string,
};

export default TypeScheduleForm;